import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { useTheme } from "@mui/material";
import { Image, Text, View } from "@react-pdf/renderer";
import {
  PDFDefaultPlatformLogoS3,
  PDFStyleConfigs,
  USE_PDF_LOCAL_ASSETS,
} from "../../../configs";
import {
  checkContainsMultipleLinesRecommendationBadge,
  checkHeadersContainsLongProductName,
  generatePDFImageIconUrl,
  shouldShowRecommendationLabel,
} from "../../../utils";
import { PDFMainBadge } from "../main-badge";
import { RecommendationLabel } from "../recommendation-label";
import { PDFTableCell } from "../table-cell";
import { PDFTableRow } from "../table-row";

export const PDFTableHeaderWithLogo = ({
  selectedProducts,
  isFeature = false,
  isDetailMode = false,
  productRecommendations,
  isReview = false,
}: {
  selectedProducts?: SelectedProductDTO[];
  isFeature?: boolean;
  isDetailMode?: boolean;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  isReview?: boolean;
}) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();

  let firstCellWidth = PDFStyleConfigs.TableFirstCellWidth;
  if (!isFeature || (isFeature && isDetailMode))
    firstCellWidth += PDFStyleConfigs.TableCellWidth;

  const containsMultipleLinesBadge =
    checkContainsMultipleLinesRecommendationBadge({
      selectedProducts,
      productRecommendations,
    });

  const containsLongProductName =
    checkHeadersContainsLongProductName(selectedProducts);

  const showRecommendationLabel = shouldShowRecommendationLabel(
    productRecommendations,
  );

  return (
    <PDFTableRow
      type={
        showRecommendationLabel || containsLongProductName
          ? "HeaderLineWithRecommendation"
          : "HeaderLine"
      }
      additionHeight={
        containsMultipleLinesBadge && containsLongProductName
          ? PDFStyleConfigs.TableRowRecommendationDoubleHeight
          : containsMultipleLinesBadge || (containsLongProductName && isReview)
          ? PDFStyleConfigs.TableRowRecommendationHeight
          : 0
      }
    >
      <PDFTableCell width={firstCellWidth}>
        <Text
          style={{
            ...typographyStyle.body2,
            color: palette.text.primary,
            fontWeight: "bold",
          }}
        >
          {isFeature ? "Feature groups and sub-groups" : "Business metrics"}
        </Text>
        {isFeature && isDetailMode && (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontWeight: "bold",
            }}
          >
            (Selections / # available)
          </Text>
        )}
      </PDFTableCell>
      {isFeature && !isDetailMode && (
        <PDFTableCell width={PDFStyleConfigs.TableCellWidth}>
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontWeight: "bold",
            }}
          >
            Selections /
          </Text>
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontWeight: "bold",
            }}
          >
            # Available
          </Text>
        </PDFTableCell>
      )}

      {selectedProducts?.map((product, index) => (
        <PDFTableCell
          width={PDFStyleConfigs.TableCellWidth}
          key={index}
          paddingHorizontal={0}
          paddingVertical={0}
        >
          <View
            style={{
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              height: "100%",
            }}
          >
            <View
              style={{
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                height: containsLongProductName
                  ? PDFStyleConfigs.TableRowHeaderLineWithRecommendation
                  : PDFStyleConfigs.TableRowHeaderLine,
                gap: 5,
                paddingVertical: 5,
                paddingHorizontal: 5,
              }}
            >
              <View
                style={{
                  position: "relative",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                {product.isProductMain && <PDFMainBadge />}
                {product?.productLogo?.url && !USE_PDF_LOCAL_ASSETS ? (
                  <Image
                    style={{
                      position: "absolute",
                      zIndex: -1,
                      width: 35,
                      height: 35,
                      marginTop: 1,
                    }}
                    src={generatePDFImageIconUrl(product?.productLogo?.url)}
                  />
                ) : (
                  <Image
                    style={{
                      position: "absolute",
                      zIndex: -1,
                      width: 35,
                      height: 35,
                      marginTop: 1,
                    }}
                    src={PDFDefaultPlatformLogoS3}
                  />
                )}
              </View>

              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontWeight: "bold",
                  textAlign: "center",
                  marginTop: 35,
                  width: PDFStyleConfigs.TableCellWidth,
                }}
              >
                {product?.productName}
              </Text>
            </View>

            <RecommendationLabel
              product={product}
              productRecommendations={productRecommendations}
              shouldIncreaseHeight={containsMultipleLinesBadge}
            />
          </View>
        </PDFTableCell>
      ))}
    </PDFTableRow>
  );
};
